import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// Request timeout configuration
const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Create fetch with timeout and retry logic
const fetchWithTimeout = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);

    // If request was aborted due to timeout
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - please check your connection and try again');
    }

    throw error;
  }
};

// Retry wrapper for network requests
const fetchWithRetry = async (
  url: string,
  options: RequestInit = {},
  attempt = 0
): Promise<Response> => {
  try {
    return await fetchWithTimeout(url, options);
  } catch (error: any) {
    const isNetworkError =
      error.message?.includes('fetch') ||
      error.message?.includes('network') ||
      error.message?.includes('timeout');

    if (isNetworkError && attempt < MAX_RETRIES) {
      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, attempt)));
      return fetchWithRetry(url, options, attempt + 1);
    }

    throw error;
  }
};

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    storageKey: 'crm-auth',
  },
  global: {
    headers: {
      'X-Client-Info': 'crm-app',
    },
    fetch: fetchWithRetry,
  },
  db: {
    schema: 'public',
  },
});